// payoutmanagement/PayoutCalendar.jsx — 달력 뷰 (날짜별 정산인원·금액·엑셀 다운로드)

import React from 'react';
import { todayKST, daysInMonth, dayOfWeekUTC } from '../../../utils/date';

// ── 숫자 포맷 ─────────────────────────────────────────────────────────────────
function fmt(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('ko-KR');
}

// ── 모바일 축약 포맷 (만원 단위) ─────────────────────────────────────────────
function fmtShort(v) {
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return '0';
  if (n >= 10000) return Math.floor(n / 10000) + '만';
  if (n >= 1000) return Math.floor(n / 1000) + '천';
  return String(n);
}

// ── 달력 생성 헬퍼 ────────────────────────────────────────────────────────────
function buildCalendarGrid(yearMonth) {
  const [y, m] = yearMonth.split('-').map(Number);
  // dayOfWeekUTC / daysInMonth 사용 (new Date 직접 사용 금지 — utils/date.js 사용)
  const startDow = dayOfWeekUTC(`${yearMonth}-01`); // 0=일, 1=월, ...
  const lastDate = daysInMonth(y, m);

  const cells = [];
  // 빈 셀 (첫 주 앞 패딩)
  for (let i = 0; i < startDow; i++) cells.push(null);
  // 날짜 셀
  for (let d = 1; d <= lastDate; d++) {
    const dd = String(d).padStart(2, '0');
    cells.push(`${yearMonth}-${dd}`);
  }
  return cells;
}

const DOW_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

export function PayoutCalendar({ month, prevMonth, nextMonth, calendarData, downloadExcel, downloadPrepayExcel, isMobile }) {
  const cells = buildCalendarGrid(month);
  // 날짜별 데이터 맵
  const dataMap = {};
  (calendarData || []).forEach((d) => { dataMap[d.date] = d; });

  const [y, m] = month.split('-').map(Number);
  const title = `${y}년 ${m}월`;

  // ── 스타일 ──────────────────────────────────────────────────────────────────
  const navSt = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 16px', background: 'var(--bg-card)',
    borderBottom: '1px solid var(--border)',
  };
  const navBtnSt = {
    border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg-card)',
    padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
    color: 'var(--text)',
  };
  const gridSt = {
    display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
    gap: 1, background: 'var(--border)', border: '1px solid var(--border)',
  };
  const dowSt = {
    padding: '8px 4px', fontSize: 11, fontWeight: 700,
    color: 'var(--text-muted)', textAlign: 'center',
    background: 'var(--bg-subtle)',
  };
  const cellSt = (hasData) => ({
    height: isMobile ? 52 : 80, padding: isMobile ? '3px 4px' : '4px 6px',
    background: hasData ? 'rgba(37,99,235,0.04)' : 'var(--bg-card)',
    display: 'flex', flexDirection: 'column', gap: isMobile ? 1 : 2,
    position: 'relative', overflow: 'hidden',
  });
  const dateSt = (isToday) => ({
    fontSize: 12, fontWeight: isToday ? 800 : 500,
    color: isToday ? '#2563eb' : 'var(--text-sub)',
  });
  const infoBadgeSt = {
    fontSize: 10, color: '#2563eb', fontWeight: 600, lineHeight: 1.3,
  };
  const amtSt = {
    fontSize: isMobile ? 9 : 10, color: '#059669', fontWeight: 700,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  };
  const xlBtnSt = {
    position: 'absolute', bottom: 3, right: 3,
    border: 'none', borderRadius: 4, padding: '2px 6px',
    background: '#059669', color: '#fff', fontSize: 9,
    fontWeight: 700, cursor: 'pointer', lineHeight: 1.4,
  };

  // 오늘 날짜
  const today = todayKST();

  return (
    <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border)', background: 'var(--bg-card)' }}>
      {/* 네비게이션 */}
      <div style={navSt}>
        <button style={navBtnSt} onClick={prevMonth}>◀ 이전</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--text)' }}>{title}</span>
          {/* 선지급분 엑셀 다운로드 — 해당 월 전체 */}
          {!isMobile && (
            <button
              style={{ ...navBtnSt, background: '#059669', color: '#fff', border: 'none', fontSize: 12 }}
              onClick={downloadPrepayExcel}
              title={`${title} 선지급분 엑셀 다운로드`}
            >
              선지급분
            </button>
          )}
        </div>
        <button style={navBtnSt} onClick={nextMonth}>다음 ▶</button>
      </div>

      {/* 요일 헤더 */}
      <div style={gridSt}>
        {DOW_LABELS.map((d, i) => (
          <div key={d} style={{ ...dowSt, color: i === 0 ? '#dc2626' : i === 6 ? '#2563eb' : dowSt.color }}>{d}</div>
        ))}

        {/* 날짜 셀 */}
        {cells.map((date, idx) => {
          if (!date) return <div key={`empty-${idx}`} style={{ background: 'var(--bg-subtle)' }} />;
          const dayNum = Number(date.slice(8));
          const info = dataMap[date];
          const isToday = date === today;

          return (
            <div key={date} style={cellSt(!!info)}>
              <span style={dateSt(isToday)}>{dayNum}</span>
              {info && (
                <>
                  <span style={infoBadgeSt}>{info.person_count}명</span>
                  <span style={amtSt}>{isMobile ? fmtShort(info.total_amount) : `${fmt(info.total_amount)}원`}</span>
                  {/* 엑셀 다운로드 버튼 — PC에서만, 인원 있을 때만 */}
                  {!isMobile && Number(info.person_count) > 0 && (
                    <button
                      style={xlBtnSt}
                      onClick={(e) => { e.stopPropagation(); downloadExcel(date); }}
                      title={`${date} 급여이체 엑셀 다운로드`}
                    >
                      XLSX
                    </button>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
